import {
  BookOpen,
  LucideCheck,
  LucideCircleCheck,
  LucideGripHorizontal,
  LucideMaximize2,
  LucideMinimize2,
  LucideRotateCcw,
  LucideSend,
  LucideSquarePen,
  LucideTrash2,
  LucideX,
} from 'lucide-react';
import React, {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../UI/tabs';
import { Textarea } from '../ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../UI/alert-dialog';
import { createNote, deleteNote, getNotes, updateNote } from '../../api/notebook';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { resolveMentionedUserIdsFromContent } from '../../helper/notebook-mentions';
import { uuid } from 'uuidv4';
import { Config } from '../../utils';
import { resizeTextarea } from '../../helper/elements';
import { invalidateOrderCache } from '../../api/order-cache';

type NotebookTabsProps = 'projects' | 'items' | 'components';
type NotebookComponentIdType =
  | 'summary_component'
  | 'fabric'
  | 'pg_build_component'
  | 'pg_total_only';

type ComponentNotebookContext = {
  componentId?: number | null;
  componentIdType?: NotebookComponentIdType | null;
  componentKey?: string | null;
  componentName?: string | null;
  componentOrderingDescription?: string | null;
};

type NotebookProps = {
  row: any;
  defaultTab: NotebookTabsProps;
  notebookTabs: NotebookTabsProps[];
  user: any;
  table: string;
  orderItemType?: any;
  orderOpen?: boolean;
  roomName?: string;
  newOrder?: boolean;
  client?: any;
  showIconTitle?: boolean;
  canAccessTeamSettings?: boolean;
  updateNotebook?(projId: number, filterOut: string): void;
  wrapperClassName?: any;
  triggerClassName?: any;
  closeOnOutsideClick?: boolean;
  componentContext?: ComponentNotebookContext | null;
};

type NotebookOpenRequest = {
  noteId: number | null;
  tabType: NotebookTabsProps | null;
} | null;

const NOTEBOOK_ACTIVE_EVENT = 'quipa:notebook-active-change';
const PG_TOTAL_ONLY_COMPONENT_KEY = 'pg:total-only';
let activeNotebookOwnerId: string | null = null;
const NOTEBOOK_COMPONENT_TEXT_KEYS = [
  'name',
  'item',
  'label',
  'displayName',
  'title',
  'value',
  'estimateDescription',
  'orderingDescription',
];

const resolveNotebookDisplayText = (value: any, depth = 0): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const normalized = String(value).trim();
    return normalized === '[object Object]' ? '' : normalized;
  }

  if (depth >= 2 || typeof value !== 'object') {
    return '';
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const normalizedItem = resolveNotebookDisplayText(item, depth + 1);
      if (normalizedItem) return normalizedItem;
    }
    return '';
  }

  for (const key of NOTEBOOK_COMPONENT_TEXT_KEYS) {
    const normalizedCandidate = resolveNotebookDisplayText(value?.[key], depth + 1);
    if (normalizedCandidate) return normalizedCandidate;
  }

  return '';
};

const dispatchNotebookActiveChange = (ownerId: string | null) => {
  activeNotebookOwnerId = ownerId;

  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(NOTEBOOK_ACTIVE_EVENT, {
      detail: { ownerId },
    })
  );
};

const clearNotebookOpenSearchParams = () => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const keys = [
    'openNotebook',
    'noteId',
    'itemId',
    'projectId',
    'notebookTable',
    'tabType',
    'componentId',
    'componentIdType',
    'componentKey',
  ];
  let changed = false;

  keys.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (changed) {
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }
};

const parseNotebookOpenRequest = (
  table: string,
  row: any,
  notebookTabs: NotebookTabsProps[],
  componentContext?: ComponentNotebookContext | null
): NotebookOpenRequest => {
  if (typeof window === 'undefined') return null;

  const search = new URLSearchParams(window.location.search);
  if (search.get('openNotebook') !== 'true') return null;

  const queryTable = search.get('notebookTable');
  if (queryTable && queryTable !== table) return null;

  const tabTypeRaw = search.get('tabType');
  const tabType =
    tabTypeRaw && notebookTabs.includes(tabTypeRaw as NotebookTabsProps)
      ? (tabTypeRaw as NotebookTabsProps)
      : null;

  const queryProjectId = Number(search.get('projectId') || 0);
  const queryItemId = Number(search.get('itemId') || 0);
  const queryComponentId = Number(search.get('componentId') || 0);
  const queryComponentIdTypeRaw = String(search.get('componentIdType') || '').trim();
  const queryComponentIdType =
    queryComponentIdTypeRaw === 'summary_component' ||
    queryComponentIdTypeRaw === 'fabric' ||
    queryComponentIdTypeRaw === 'pg_build_component' ||
    queryComponentIdTypeRaw === 'pg_total_only'
      ? queryComponentIdTypeRaw
      : null;
  const queryComponentKey = String(search.get('componentKey') || '').trim() || null;

  if (table === 'projects') {
    if (queryProjectId <= 0) return null;
    if (Number(row?.id) !== queryProjectId) return null;
  }

  if (table === 'items') {
    if (queryItemId <= 0) return null;
    if (Number(row?.id) !== queryItemId) return null;

    const expectsComponentContext = tabType === 'components' || queryComponentId > 0 || Boolean(queryComponentKey);
    if (expectsComponentContext) {
      const rowComponentId = Number(componentContext?.componentId || 0);
      const rowComponentIdType = componentContext?.componentIdType || null;
      const rowComponentKey = String(componentContext?.componentKey || '').trim() || null;
      if (queryComponentId <= 0 && !queryComponentKey) return null;
      if (queryComponentId > 0 && (rowComponentId <= 0 || rowComponentId !== queryComponentId)) {
        return null;
      }
      if (queryComponentKey && rowComponentKey !== queryComponentKey) return null;
      if (queryComponentIdType && rowComponentIdType !== queryComponentIdType) return null;
    }
  }

  const noteIdRaw = Number(search.get('noteId') || 0);

  return {
    noteId: noteIdRaw > 0 ? noteIdRaw : null,
    tabType,
  };
};

const Notebook = forwardRef(
  (
    {
      row,
      defaultTab = 'projects',
      notebookTabs = ['projects'],
      user,
      table,
      orderItemType,
      orderOpen,
      roomName,
      newOrder = false,
      client,
      showIconTitle,
      canAccessTeamSettings,
      updateNotebook,
      wrapperClassName,
      triggerClassName,
      closeOnOutsideClick = true,
      componentContext = null,
    }: NotebookProps,
    ref: any
  ) => {
    const suggestionsRef = useRef<HTMLDivElement | null>(null);
    const suggestionItemRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const notebookOwnerIdRef = useRef<string>(uuid());
    const notesContainerRef = useRef<any>();
    const newNoteRef = useRef<any>();
    const editNoteRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
    const dragRef = useRef<{
      dragging: boolean;
      startMouseX: number;
      startMouseY: number;
      startRight: number;
      startBottom: number;
    }>({
      dragging: false,
      startMouseX: 0,
      startMouseY: 0,
      startRight: 0,
      startBottom: 0,
    });
    const rowId = Number(row?.id || 0);
    const hasRowId = Number.isInteger(rowId) && rowId > 0;
    const effectiveNewOrder = Boolean(newOrder || (table === 'items' && !hasRowId));
    const componentContextType = String(componentContext?.componentIdType || '').trim();
    const componentContextId = Number(componentContext?.componentId || 0);
    const componentContextKey = String(componentContext?.componentKey || '').trim();
    const componentContextName = resolveNotebookDisplayText(componentContext?.componentName);
    const componentContextOrderingDescription = resolveNotebookDisplayText(
      componentContext?.componentOrderingDescription
    );
    const hasComponentContext = Boolean(
      componentContextType &&
        ((componentContextId > 0 && componentContextType !== 'pg_total_only') ||
          (componentContextType === 'pg_total_only' &&
            componentContextKey === PG_TOTAL_ONLY_COMPONENT_KEY))
    );

    const availableNotebookTabs = useMemo(() => {
      const sourceTabs = Array.isArray(notebookTabs) ? notebookTabs : [];
      const filteredTabs = sourceTabs.filter((tabName) => {
        if (tabName === 'items') {
          return !(table === 'items' && !hasRowId && !newOrder);
        }
        if (tabName === 'components') {
          if (table === 'items' && !hasRowId) return false;
          return hasComponentContext;
        }
        return true;
      });

      if (filteredTabs.length > 0) return filteredTabs;
      if (sourceTabs.includes('projects')) return ['projects'];
      if (sourceTabs.includes('items')) return ['items'];
      if (sourceTabs.includes('components')) {
        // If component scope is unavailable, prefer a non-component tab so the notebook is still navigable.
        return hasRowId ? ['items'] : ['projects'];
      }
      return ['projects'];
    }, [notebookTabs?.join(','), table, hasRowId, hasComponentContext]);

    const resolvedDefaultTab = availableNotebookTabs.includes(defaultTab)
      ? defaultTab
      : availableNotebookTabs[0] || 'projects';

    const buildDefaultShowResolvedState = () => {
      let new_obj = {};
      availableNotebookTabs?.forEach((i: any) => {
        new_obj[i] = false;
      });
      return new_obj;
    };

    // *** STATES ***
    const [open, setOpen] = useState<boolean>(false);
    const [checkDeleted, setCheckDeleted] = useState<boolean>(false);
    const [mention, setMention] = useState<any>(null);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [position, setPosition] = useState<{
      right: number;
      bottom: number;
    }>({ right: 16, bottom: 16 });
    const [newNoteValue, setNewNoteValue] = useState<string>('');
    const [tab, setTab] = useState<any>(resolvedDefaultTab);
    const [showResolved, setShowResolved] = useState<{
      projects?: boolean;
      items?: boolean;
      components?: boolean;
    }>(() => buildDefaultShowResolvedState());

    const [data, setData] = useState<{
      items: any[];
      projects: any[];
      components: any[];
    }>({
      items: [],
      projects: [],
      components: [],
    });
    const [editNote, setEditNote] = useState<{
      id: string | null;
      content: string;
      type: 'projects' | 'items' | string;
    }>({
      id: null,
      content: '',
      type: '',
    });
    const [maximized, setMaximized] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);
    const [highlightNoteId, setHighlightNoteId] = useState<number | null>(null);
    const teamMembers = user?.teamContext?.teamMembers || [];

    const resolveProjectIdForTab = (targetTab: NotebookTabsProps | string | null | undefined) => {
      if (table === 'projects') {
        return hasRowId ? rowId : null;
      }
      if (table === 'items') {
        if (targetTab !== 'projects') return null;
        const projectId = effectiveNewOrder ? Number(client?.id || 0) : Number(row?.client?.id || 0);
        return Number.isInteger(projectId) && projectId > 0 ? projectId : null;
      }
      return null;
    };

    const resolveOrderIdForTab = (targetTab: NotebookTabsProps | string | null | undefined) => {
      if (table !== 'items') return null;
      if (targetTab === 'projects') return null;
      return hasRowId ? rowId : null;
    };

    const canPersistTabToServer = (targetTab: NotebookTabsProps | string | null | undefined) => {
      if (table === 'projects') return hasRowId;
      if (table === 'items') {
        if (targetTab === 'projects') {
          return resolveProjectIdForTab(targetTab) !== null;
        }
        return hasRowId;
      }
      return false;
    };

    const invalidateCurrentOrderCache = () => {
      if (table !== 'items') return;
      if (!hasRowId) return;
      invalidateOrderCache(rowId);
    };
    // *** END OF STATES ***
    // *** FUNCTIONS ***
    const getMentionQuery = (text: string, caret: number) => {
      const before = text.slice(0, caret);
      const m = before.match(/(^|\s)@([a-zA-Z0-9_-]*)$/);
      if (!m) return null;

      const fullMatch = m[0];
      const query = m[2] ?? '';

      return {
        query,
        startIndex: caret - fullMatch.length + (fullMatch.startsWith(' ') ? 1 : 0),
        endIndex: caret,
      };
    };

    const updateMention = (
      nextValue: string,
      caret: number,
      mode: 'new' | 'edit',
      noteId?: any
    ) => {
      const m = getMentionQuery(nextValue, caret);
      setMention(m ? { ...m, mode, noteId: noteId ?? null } : null);
      if (m) {
        setOpen(true);
        setActiveIndex(0);
      } else {
        setOpen(false);
      }
    };

    // Filter notes
    const handleShowResolved = () => {
      setShowResolved((prev) => {
        return {
          ...prev,
          [tab]: !prev?.[tab],
        };
      });
    };

    // Edit or cancel edit a note
    const handleEditNote = (item: any) => {
      setEditNote({
        id: item ? item?.id : null,
        content: item ? item?.content : '',
        type: tab,
      });
      setOpen(false);
      setMention(null);
    };

    // Update existing note
    const handleUpdateNote = async () => {
      if (!editNote.content) return;
      if (!editNote.id) return;

      setData((prev: any) => {
        return {
          ...prev,
          [tab]: prev?.[tab]?.map((i: any) => {
            if (i.id === editNote.id) {
              return {
                ...i,
                content: editNote.content,
              };
            }
            return i;
          }),
        };
      });

      const updateMentionedUserIds = resolveMentionedUserIdsFromContent(
        editNote.content,
        teamMembers
      );

      const api_payload = {
        id: editNote.id,
        content: editNote.content,
        mentionedUserIds: updateMentionedUserIds,
        table,
      };

      if (canPersistTabToServer(tab)) {
        const res = await updateNote(api_payload);
        if (res) {
          // toast('Note updated');
          invalidateCurrentOrderCache();
          handleRefetch();
          setEditNote({
            id: null,
            content: '',
            type: '',
          });
        }
      } else {
        setData((prev: any) => {
          return {
            ...prev,
            [tab]: prev?.[tab]?.map((i: any) => {
              if (i.id === editNote.id) {
                return {
                  ...i,
                  content: editNote.content,
                };
              }
              return i;
            }),
          };
        });
        setEditNote({
          id: null,
          content: '',
          type: '',
        });
      }
    };

    // Handle value of the editing note
    const handleChangeEditNote = (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value, selectionStart } = e.target;
      const caret = selectionStart ?? value.length;
      resizeTextarea(e.target, 60);
      setEditNote((prev) => ({ ...prev, content: value }));
      if (canAccessTeamSettings) {
        updateMention(value, caret, 'edit', editNote.id);
      }
    };

    // Delete note
    const handleDeleteNote = async (item: any) => {
      if (!item?.id) return;

      setData((prev: any) => {
        return {
          ...prev,
          [tab]: prev?.[tab]?.filter((f: any) => f.id !== item?.id),
        };
      });

      if (canPersistTabToServer(tab)) {
        const res = await deleteNote(item.id);
        if (res) {
          invalidateCurrentOrderCache();
          setCheckDeleted(true);
          // toast('Note deleted');
        }
      } else {
        setData((prev: any) => {
          return {
            ...prev,
            [tab]: prev?.[tab]?.filter((i: any) => i.id !== item.id),
          };
        });
        setCheckDeleted(true);
      }
    };

    // Crate new note for project/items notebook
    const handleCreateNewNote = async () => {
      if (!newNoteValue?.trim()) return;
      if (loading) return;

      const mentionedUserIds = resolveMentionedUserIdsFromContent(newNoteValue, teamMembers) || [];
      handleLoading(true);

      const projectId = resolveProjectIdForTab(tab);
      const orderId = resolveOrderIdForTab(tab);
      const componentId = tab === 'components' ? Number(componentContext?.componentId || 0) : null;
      const componentIdType = tab === 'components' ? componentContext?.componentIdType || null : null;
      const componentKey =
        tab === 'components' ? String(componentContext?.componentKey || '').trim() || null : null;
      const hasComponentScope =
        tab === 'components' &&
        Boolean(
          componentIdType &&
            ((componentId && componentId > 0) || (componentKey && componentKey.length > 0))
        );

      let api_payload = {
        tabType: tab,
        table: table,
        creatorId: user?.id,
        content: newNoteValue || null,
        resolved: false,
        resolvedById: null,
        orderId: orderId,
        projectId: projectId,
        componentId: hasComponentScope && componentId && componentId > 0 ? componentId : null,
        componentIdType: hasComponentScope ? componentIdType : null,
        componentKey: hasComponentScope ? componentKey : null,
        mentionedUserIds: mentionedUserIds,
      };

      if (canPersistTabToServer(tab)) {
        setNewNoteValue('');
        const noteRes = await createNote(api_payload);
        if (noteRes) {
          if (table === 'projects' && row) {
            const existingNotebook = Array.isArray(row?.notebook) ? row.notebook : [];
            const alreadyExists = existingNotebook.some(
              (note: any) => String(note?.id) === String(noteRes?.id)
            );
            if (!alreadyExists) {
              row.notebook = [...existingNotebook, noteRes];
            }
          }
          // toast('Note created');
          invalidateCurrentOrderCache();
          handleRefetch();
        }
      } else {
        setNewNoteValue('');
        let new_order_payload = {
          id: uuid(),
          tabType: tab,
          table: table,
          type: tab,
          creator: user,
          content: newNoteValue || null,
          resolved: false,
          resolvedBy: null,
          orderId: orderId || null,
          projectId: projectId || null,
          componentId:
            hasComponentScope && componentId && componentId > 0 ? componentId : null,
          componentIdType: hasComponentScope ? componentIdType : null,
          componentKey: hasComponentScope ? componentKey : null,
          mentionedUserIds: mentionedUserIds,
          notebookMentions:
            teamMembers?.map((i: any) => {
              if (mentionedUserIds?.includes(i?.userId)) {
                return {
                  mentionUser: {
                    id: i?.userId,
                    label: i.label,
                  },
                };
              }
            }) || [],
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setData((prev: any) => {
          return {
            ...prev,
            [tab]: [...prev?.[tab], new_order_payload],
          };
        });
        handleLoading(false);
      }
    };
    // Change resolved status on note
    const handleChangeResolveStatus = async (item: any, value: boolean) => {
      if (!item) return;
      if (!user?.id) return;

      setEditNote({
        id: null,
        content: '',
        type: '',
      });

      setData((prev: any) => {
        return {
          ...prev,
          [tab]: prev?.[tab]?.map((i: any) => {
            if (i?.id === item?.id) {
              return {
                ...i,
                resolved: value,
                resolvedBy: {
                  firstName: user?.firstName || '',
                  lastName: user?.lastName || '',
                  label: user?.label || '',
                },
              };
            }
            return i;
          }),
        };
      });

      const api_payload = {
        id: item?.id,
        resolved: value,
        resolvedById: value ? user?.id : null,
        table,
      };

      if (canPersistTabToServer(tab)) {
        const res = await updateNote(api_payload);
        if (res) {
          invalidateCurrentOrderCache();
          let toast_message = value ? 'Note marked as resolved' : 'Note unresolved';
          toast(toast_message);
        }
      } else {
        setData((prev: any) => {
          return {
            ...prev,
            [tab]: prev?.[tab]?.map((i: any) => {
              if (i.id === item.id) {
                return {
                  ...i,
                  resolved: value,
                  resolvedBy: value ? user : null,
                };
              }
              return i;
            }),
          };
        });
        let toast_message = value ? 'Note marked as resolved' : 'Note unresolved';
        toast(toast_message);
      }
    };

    const handleLoading = (val: boolean) => {
      setLoading(val);
    };

    const handleRefetch = () => {
      setRefetch((prev) => !prev);
    };

    // Change tabs between projects and items notebook content
    const handleChangeTab = (val: string) => {
      setTab(val);
    };

    // Set value for new note
    const handleChangeNote = (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value, selectionStart } = e.target;
      const ceret = selectionStart ?? value.length;
      resizeTextarea(e.target, 60);
      setNewNoteValue(value);
      if (canAccessTeamSettings) {
        updateMention(value, ceret, 'new');
      }
    };

    const insertMention = (item: any) => {
      const inserted = `@${item?.label} `;
      if (!mention) return;

      if (mention.mode === 'edit' && mention.noteId) {
        const before = editNote.content.slice(0, mention.startIndex);
        const after = editNote.content.slice(mention.endIndex);
        const next = before + inserted + after;

        setEditNote((prev) => ({ ...prev, content: next }));
        setOpen(false);
        setMention(null);

        requestAnimationFrame(() => {
          const el = editNoteRefs.current[String(mention.noteId)];
          const pos = (before + inserted).length;
          if (!el) return;
          el.focus();
          el.setSelectionRange(pos, pos);
        });
        return;
      }

      if (!newNoteRef.current) return;

      const before = newNoteValue.slice(0, mention.startIndex);
      const after = newNoteValue.slice(mention.endIndex);
      const next = before + inserted + after;

      setNewNoteValue(next);
      setOpen(false);
      setMention(null);

      requestAnimationFrame(() => {
        const el = newNoteRef.current;
        const pos = (before + inserted).length;
        el.focus();
        el.setSelectionRange(pos, pos);
      });
    };

    const suggestions = useMemo(() => {
      if (!mention) return [];

      const q = mention.query.toLowerCase().trim();

      return teamMembers
        .filter((u: any) => {
          const first = (u.firstName || '').toLowerCase();
          const last = (u.lastName || '').toLowerCase();
          const label = (u.label || '').toLowerCase();

          // If q is empty => show all
          if (!q) return true;

          return first.includes(q) || last.includes(q) || label.includes(q);
        })
        .slice(0, 8);
    }, [mention, teamMembers]);

    // Shift + Enter -> create new note etc...
    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>, sendType: any) => {
      if (!loading && e.key === 'Enter' && e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        if (sendType === 'new') {
          handleCreateNewNote();
        } else if (sendType === 'edit') {
          handleUpdateNote();
        }
        return;
      }

      if (canAccessTeamSettings) {
        if (e.key === 'Escape') {
          e.preventDefault();
          setOpen(false);
          setMention(null);
          return;
        }

        if (!suggestions.length || !mention || mention.mode !== sendType) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          // only hijack Enter if mention is active
          e.preventDefault();
          insertMention(suggestions[activeIndex]);
        }
      }
    };

    const handleBlur = (elCase: string) => {
      switch (elCase) {
        case 'newNote':
          setTimeout(() => {
            setOpen(false);
          }, 120);
          break;
        case 'editNote':
          setTimeout(() => {
            setOpen(false);
          }, 120);
          break;
        default:
          break;
      }
    };

    // On notebooks container mouse down trigger dragging
    const onMouseDown = (e: any) => {
      e.preventDefault();
      dragRef.current.dragging = true;
      dragRef.current.startMouseX = e.clientX;
      dragRef.current.startMouseY = e.clientY;
      dragRef.current.startRight = position.right;
      dragRef.current.startBottom = position.bottom;
    };

    const handleResetNotebook = () => {
      setOpen(false);
      setMention(null);
      setActiveIndex(0);
      setMaximized(false);
      setActive(false);
      setPosition({ right: 16, bottom: 16 });
      setTab(resolvedDefaultTab);
      setNewNoteValue('');
      setShowResolved(() => buildDefaultShowResolvedState());
      setEditNote({
        id: null,
        content: '',
        type: '',
      });
      setHighlightNoteId(null);
    };

    const handleCloseNotebook = ({ releaseOwnership = true }: { releaseOwnership?: boolean } = {}) => {
      handleResetNotebook();

      if (releaseOwnership && activeNotebookOwnerId === notebookOwnerIdRef.current) {
        dispatchNotebookActiveChange(null);
      }
    };

    const handleOpenNotebook = (initialTab?: NotebookTabsProps | null) => {
      if (activeNotebookOwnerId !== notebookOwnerIdRef.current) {
        dispatchNotebookActiveChange(notebookOwnerIdRef.current);
      }

      const nextTab =
        initialTab && availableNotebookTabs.includes(initialTab)
          ? initialTab
          : resolvedDefaultTab;
      setTab(nextTab);
      setActive(true);
    };

    const handleRecursiveParent = (client: any) => {
      if (!client?.parentClient) return [];

      return [client?.parentClient, ...handleRecursiveParent(client?.parentClient)];
    };

    const handleFetchNotes = async (payload: any) => {
      if (!payload) return;
      handleLoading(true);
      const res = await getNotes(payload);
      if (res) {
        if (table === 'projects' && tab === 'projects' && row) {
          row.notebook = Array.isArray(res) ? [...res] : [];
        }
        setData((prev: any) => {
          return {
            ...prev,
            [tab]: res,
          };
        });
        handleLoading(false);
      }
    };

    const highlightMentions = (text: string, note: any) => {
      if (!text) return text;

      const mentionLabels = new Set<string>(
        (note?.notebookMentions || [])
          .map((m: any) => (m?.mentionUser?.label || '').trim())
          .filter(Boolean)
          .map((label: string) => label.toLowerCase())
      );

      if (!mentionLabels.size) return text;

      const chunks = text.split(/(\s+)/);
      return chunks.map((chunk: string, index: number) => {
        if (!chunk || /^\s+$/.test(chunk))
          return <React.Fragment key={index}>{chunk}</React.Fragment>;
        if (!chunk.startsWith('@')) return <React.Fragment key={index}>{chunk}</React.Fragment>;

        const tokenBody = chunk.slice(1);
        const trailingMatch = tokenBody.match(/[.,!?;:)\]}]+$/);
        const trailing = trailingMatch?.[0] || '';
        const coreLabel = trailing ? tokenBody.slice(0, -trailing.length) : tokenBody;
        const normalized = coreLabel.trim().toLowerCase();

        if (!normalized || !mentionLabels.has(normalized)) {
          return <React.Fragment key={index}>{chunk}</React.Fragment>;
        }

        return (
          <React.Fragment key={index}>
            <span className="bg-primary/20 text-primary font-medium px-1 rounded">
              @{coreLabel}
            </span>
            {trailing}
          </React.Fragment>
        );
      });
    };
    // *** END OF FUNCTIONS ***

    // *** USE EFFECTS ***
    // Scroll up to bottom for newest content
    useEffect(() => {
      if (!active || loading) return;

      if (!highlightNoteId) {
        requestAnimationFrame(() => {
          bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        });
      }
    }, [active, loading, data, tab, showResolved]);

    useEffect(() => {
      if (checkDeleted) {
        if (table === 'items') {
          if (data?.['items']?.length <= 0) {
            if (updateNotebook) {
              updateNotebook(row?.id, 'items');
            }
          }
        } else if (table === 'projects') {
          if (data?.['projects']?.length <= 0) {
            if (updateNotebook) {
              updateNotebook(row?.id, 'projects');
            }
          }
        }
        setCheckDeleted(false);
      }
    }, [checkDeleted]);

    // If edit note is active then scroll to that note
    useEffect(() => {
      if (editNote?.id) {
        let currEditTextArea = editNoteRefs?.current?.[String(editNote?.id)];
        if (currEditTextArea) {
          currEditTextArea.style.height = 'auto';
          const maxHeight = (50 * window.innerHeight) / 100 - 130;
          currEditTextArea.style.height = `${Math.min(currEditTextArea.scrollHeight, maxHeight)}px`;
          currEditTextArea.focus()
          currEditTextArea.selectionStart = currEditTextArea.selectionEnd = currEditTextArea.value.length
          currEditTextArea.scrollTop = currEditTextArea.scrollHeight
        }

        let el = document.getElementById(`edit_note_${editNote?.id}`);
        if (!el) return;
        requestAnimationFrame(() => {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        });
      }
    }, [editNote?.id]);

    // Fetch notes
    useEffect(() => {
      if (active) {
        let api_payload = {
          tabType: tab,
          table: table,
        };
        if (table === 'projects') {
          api_payload['projectId'] = resolveProjectIdForTab(tab);
        } else if (table === 'items') {
          api_payload['projectId'] = resolveProjectIdForTab(tab);
          api_payload['orderId'] = resolveOrderIdForTab(tab);
          if (tab === 'components') {
            const componentId = Number(componentContext?.componentId || 0) || null;
            const componentType = componentContext?.componentIdType || null;
            const componentKey = String(componentContext?.componentKey || '').trim() || null;
            const hasComponentScope = Boolean(
              componentType &&
                ((componentId && componentId > 0) || (componentKey && componentKey.length > 0))
            );
            api_payload['componentId'] =
              hasComponentScope && componentId && componentId > 0 ? componentId : null;
            api_payload['componentIdType'] = hasComponentScope ? componentType : null;
            api_payload['componentKey'] = hasComponentScope ? componentKey : null;
          }
        }

        if (canPersistTabToServer(tab)) {
          handleFetchNotes(api_payload);
        }
      }
    }, [active, tab, refetch]);

    useEffect(() => {
      if (!active) return;
      const interval = window.setInterval(() => {
        handleRefetch();
      }, 12000);

      return () => {
        window.clearInterval(interval);
      };
    }, [active]);

    const locationSearch = typeof window === 'undefined' ? '' : window.location.search;
    const notebookOpenRequest = useMemo(
      () => parseNotebookOpenRequest(table, row, availableNotebookTabs, componentContext),
      [
        table,
        row?.id,
        availableNotebookTabs?.join(','),
        locationSearch,
        componentContext?.componentId,
        componentContext?.componentIdType,
        componentContext?.componentKey,
      ]
    );

    useEffect(() => {
      if (!notebookOpenRequest) return;

      if (notebookOpenRequest.tabType && notebookOpenRequest.tabType !== tab) {
        setTab(notebookOpenRequest.tabType);
      }

      if (!active) {
        handleOpenNotebook(notebookOpenRequest.tabType || undefined);
      }

      if (notebookOpenRequest.noteId) {
        setHighlightNoteId(notebookOpenRequest.noteId);
      }

      clearNotebookOpenSearchParams();
    }, [notebookOpenRequest, tab, active]);

    useEffect(() => {
      if (typeof window === 'undefined') return;

      const handleNotebookActiveChange = (event: Event) => {
        const customEvent = event as CustomEvent<{ ownerId?: string | null }>;
        const nextOwnerId = customEvent.detail?.ownerId ?? null;

        if (!nextOwnerId || nextOwnerId === notebookOwnerIdRef.current || !active) return;

        handleCloseNotebook({ releaseOwnership: false });
      };

      window.addEventListener(NOTEBOOK_ACTIVE_EVENT, handleNotebookActiveChange as EventListener);

      return () => {
        window.removeEventListener(
          NOTEBOOK_ACTIVE_EVENT,
          handleNotebookActiveChange as EventListener
        );
      };
    }, [active, resolvedDefaultTab, availableNotebookTabs?.join(',')]);

    useEffect(() => {
      return () => {
        if (activeNotebookOwnerId === notebookOwnerIdRef.current) {
          activeNotebookOwnerId = null;
        }
      };
    }, []);

    useEffect(() => {
      if (!active || !highlightNoteId) return;
      if (!data?.[tab]?.length) return;

      const noteEl = notesContainerRef.current?.querySelector?.(
        `[data-note-id="${highlightNoteId}"]`
      );
      if (!noteEl || typeof noteEl.scrollIntoView !== 'function') return;

      noteEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [active, highlightNoteId, data, tab]);

    useEffect(() => {
      if (!effectiveNewOrder) return;
      if (!row || !orderOpen) {
        setData({
          items: [],
          projects: [],
          components: [],
        });
      }
    }, [effectiveNewOrder, row, orderOpen]);

    useEffect(() => {
      if (availableNotebookTabs.includes(tab)) return;
      setTab(resolvedDefaultTab);
    }, [tab, resolvedDefaultTab, availableNotebookTabs?.join(',')]);

    // Reset editing on tab change
    useEffect(() => {
      setEditNote({
        id: null,
        content: '',
        type: '',
      });
    }, [tab]);

    // Drag the notebook container
    useEffect(() => {
      // On mousemove start moving notebook
      const onMouseMove = (e: any) => {
        if (!dragRef.current.dragging) return;

        const dx = e.clientX - dragRef.current.startMouseX;
        const dy = e.clientY - dragRef.current.startMouseY;

        setPosition({
          right: dragRef.current.startRight - dx,
          bottom: dragRef.current.startBottom - dy,
        });
      };

      // On mouseup stop dragging the notebook
      const onMouseUp = () => {
        dragRef.current.dragging = false;
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);

      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
    }, []);
    // *** END OF USE EFFECTS ***

    // *** CONSTANTS ***
    const has_notes = data?.[tab]?.length > 0;
    const all_resolved = data?.[tab]?.filter((f: any) => !f.resolved)?.length <= 0;
    const has_resolved_notes = data?.[tab]?.filter((f: any) => f.resolved)?.length > 0;
    const rowNotebookNotes = Array.isArray(row?.notebook) ? row.notebook : [];
    const rowHasNotesForVisibleTabs = rowNotebookNotes.some((note: any) =>
      availableNotebookTabs.includes(note?.type)
    );
    const hasFetchedNotesForVisibleTabs = availableNotebookTabs.some(
      (tabName) => (data?.[tabName] || []).length > 0
    );
    const noteMatchesComponentContext = (note: any) => {
      if (!hasComponentContext || note?.type !== 'components') return false;

      const noteComponentType = String(note?.componentIdType || '').trim();
      const noteComponentId = Number(note?.componentId || 0);
      const noteComponentKey = String(note?.componentKey || '').trim();

      if (componentContextType === 'pg_total_only') {
        return (
          noteComponentType === 'pg_total_only' &&
          noteComponentKey === PG_TOTAL_ONLY_COMPONENT_KEY
        );
      }

      if (componentContextType === 'pg_build_component') {
        return noteComponentType === 'pg_build_component' && noteComponentId === componentContextId;
      }

      if (componentContextType === 'fabric') {
        return noteComponentType === 'fabric' && noteComponentId === componentContextId;
      }

      if (componentContextType === 'summary_component') {
        return (
          noteComponentId === componentContextId &&
          (noteComponentType === 'summary_component' || !noteComponentType)
        );
      }

      return false;
    };
    const rowHasScopedComponentNotes = rowNotebookNotes.some(noteMatchesComponentContext);
    const fetchedComponentNotes = Array.isArray(data?.components) ? data.components : [];
    const hasFetchedScopedComponentNotes = fetchedComponentNotes.some(noteMatchesComponentContext);
    const has_saved_notes = hasComponentContext
      ? rowHasScopedComponentNotes || hasFetchedScopedComponentNotes
      : table === 'items'
        ? hasFetchedNotesForVisibleTabs || rowHasNotesForVisibleTabs
        : table === 'projects'
          ? has_notes || rowHasNotesForVisibleTabs
          : false;
    const project_name = row?.client?.displayName || '';
    const project_parent_names = handleRecursiveParent(row?.client);
    const item_type_name =
      row?.purchasedGoodsItemType?.name || row?.itemType?.name || row?.itemType || '';
    const order_type = row?.orderType
      ? row?.orderType === 'roman-shade-order'
        ? 'Roman Shade'
        : row?.orderType === 'custom-order'
          ? item_type_name
          : row?.orderType === 'purchased-goods'
            ? item_type_name
            : 'Drapery'
      : '';
    const normalizedComponentContextName =
      componentContextName && componentContextName !== '-' ? componentContextName : '';
    const normalizedComponentContextOrderingDescription =
      componentContextOrderingDescription && componentContextOrderingDescription !== '-'
        ? componentContextOrderingDescription
        : '';
    const componentContextHeaderValue = [
      normalizedComponentContextName,
      normalizedComponentContextOrderingDescription,
    ]
      .filter(Boolean)
      .join(' / ');

    const show = canAccessTeamSettings && open && mention && suggestions.length > 0;
    const showNewSuggestion = canAccessTeamSettings && show && mention?.mode === 'new';
    const showEditSuggestion = (noteId: any) =>
      show && mention?.mode === 'edit' && String(mention?.noteId) === String(noteId);

    // Scroll the suggestions with arrow keys
    useEffect(() => {
      if (!show) return;

      const el = suggestionItemRefs.current[activeIndex];
      if (!el) return;

      el.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, show, suggestions.length]);

    // Extracting data for the parents
    useImperativeHandle(ref, () => ({
      getData: () => {
        return data;
      },
    }));
    // *** END OF CONSTANTS ***
    // *** RENDER ***
    return (
      <>
        <div className={`${wrapperClassName}`}>
          <button
            data-qa={`notebook_${row?.id}_notebook_icon_trigger`}
            onClick={() => {
              if (active) {
                handleCloseNotebook();
                return;
              }

              handleOpenNotebook();
            }}
            type="button"
            disabled={row?.archived}
            className={`${showIconTitle ? 'w-full border border-input hover:border-input-hover hover:text-accent-foreground min-h-[40px] px-4 py-2' : ''} group inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:hover:bg-accent hover:text-accent-foreground rounded-md h-8 w-8 p-0 ${triggerClassName}`}
          >
            <BookOpen
              className={`${has_saved_notes ? 'text-primary group-hover:text-primary' : 'text-gray-400 group-hover:text-gray-400'} shrink-0`}
              style={{ color: has_saved_notes ? 'rgb(var(--primary))' : 'rgb(156 163 175)' }}
            />
            {showIconTitle && 'Notebook'}
          </button>
        </div>

        {active &&
          createPortal(
          <div
            data-active={active ? 'open' : 'closed'}
            id={`notebook_${row?.id}`}
            className={`fixed z-[99999999] bg-[#f9f4d9] rounded-lg shadow-xl border border-border flex flex-col data-[active=open]:pointer-events-auto data-[active=open]:select-auto data-[active=closed]:opacity-0 data-[active=closed]:pointer-events-none data-[active=closed]:select-none data-[active=closed]:animate-animate-out`}
            style={{
              right: `${maximized ? 16 : position.right}px`,
              bottom: `${maximized ? 16 : position.bottom}px`,
              width: '480px',
              height: 'calc(100vh - 130px)',
              ...(maximized
                ? {
                    top: '16px',
                    left: '16px',
                    width: 'auto',
                    height: 'auto',
                  }
                : {}),
            }}
          >
            <div
              id="header"
              onMouseDown={(e) => {
                if (maximized) return;
                onMouseDown(e);
              }}
              className={`flex items-center gap-2 px-4 py-3 border-b border-border/50 ${maximized ? 'cursor-auto' : 'cursor-move'}`}
            >
              {!maximized && (
                <LucideGripHorizontal className="lucide lucide-grip-horizontal h-4 w-4 text-muted-foreground/50" />
              )}

              <span className="font-semibold text-foreground flex-1 text-[16px] text-start">
                Notebook
              </span>

              <button
                data-qa={`notebook_maximize_minimize_trigger`}
                onMouseDown={(e) => {
                  setMaximized((prev) => !prev);
                  e.stopPropagation();
                }}
                type="button"
                className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-6 w-6"
              >
                {maximized ? (
                  <LucideMinimize2 className="lucide lucide-minimize2 h-4 w-4" />
                ) : (
                  <LucideMaximize2 className="lucide lucide-maximize2 h-4 w-4" />
                )}
              </button>

              <button
                data-qa={`notebook_close_trigger`}
                onMouseDown={(e) => {
                  handleCloseNotebook();
                  e.stopPropagation();
                }}
                type="button"
                className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-6 w-6"
              >
                <LucideX className="lucide lucide-x h-4 w-4" />
              </button>
            </div>

            <div id="title" className="px-4 py-2 border-b border-border/30 bg-[#f5f0d4] text-start">
              <div
                style={{
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'break-spaces',
                }}
                className="text-sm font-medium text-foreground"
              >
                {project_name}
                {project_parent_names?.length > 0 && ' / '}
                {project_parent_names?.map((c: any, idx: any) => {
                  return `${c?.displayName} ${idx === project_parent_names?.length - 1 ? '' : ' / '}`;
                })}
              </div>

              {table === 'items' && (
                <>
                  <div className="text-xs text-muted-foreground">
                    Item: {row ? order_type : orderItemType ? orderItemType : ''}
                    {table === 'items'
                      ? roomName || row?.roomName
                        ? ` - ${roomName || row?.roomName}`
                        : ''
                      : item_type_name
                        ? ` - ${item_type_name}`
                        : ''}
                  </div>
                  {tab === 'components' && (
                    <div className="text-xs text-muted-foreground">
                      Component: {componentContextHeaderValue || '-'}
                    </div>
                  )}
                </>
              )}
            </div>

            <Tabs
              value={tab}
              onValueChange={(value) => handleChangeTab(value)}
              className="flex-1 flex flex-col min-h-0"
            >
              <TabsList
                className={`items-center justify-center rounded-md bg-muted p-1 text-muted-foreground mx-4 mt-2 grid w-auto h-8 ${availableNotebookTabs?.length >= 3 ? 'grid-cols-3' : availableNotebookTabs?.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}
              >
                {availableNotebookTabs?.length > 0 &&
                  availableNotebookTabs?.map((item: any, idx: number) => {
                    const tabTitle =
                      item === 'projects'
                        ? 'Project Notes'
                        : item === 'items'
                          ? 'This Item'
                          : item === 'components'
                            ? 'This Component'
                            : 'Tab';
                    return (
                      <TabsTrigger
                        disabled={item === 'projects' && newOrder && !client}
                        value={item}
                        key={idx}
                        data-qa={`notebook_${item}_tab_trigger`}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-0 font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm text-xs"
                      >
                        {tabTitle}
                      </TabsTrigger>
                    );
                  })}
              </TabsList>

              {availableNotebookTabs?.length > 0 &&
                availableNotebookTabs?.map((item: any, idx: number) => {
                  return (
                    <TabsContent
                      value={item}
                      key={idx}
                      className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex-1 flex flex-col min-h-0 mt-0 pt-0 data-[state=active]:pt-0 data-[state=inactive]:hidden"
                    >
                      {loading ? (
                        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                          <div className="flex-1 min-h-0 overflow-y-auto">
                            <div className="space-y-3 p-4">
                              <span className="block text-muted-foreground text-sm w-full text-center">
                                Loading notes...
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : has_notes ? (
                        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                          <div ref={notesContainerRef} className="flex-1 min-h-0 overflow-y-auto">
                            <div className="space-y-3 p-4">
                              {!showResolved?.[tab] && all_resolved ? (
                                <span className="block text-muted-foreground text-sm w-full text-center">
                                  All notes are resolved
                                </span>
                              ) : (
                                data?.[tab]
                                  ?.filter((f: any) => (showResolved?.[tab] ? true : !f.resolved))
                                  ?.map((note: any, index: number) => {
                                    const noteCreatedAt = new Date(
                                      note?.createdAt
                                    ).toLocaleString();
                                    const noteUpdatedAt = new Date(
                                      note?.updatedAt
                                    ).toLocaleString();

                                    return (
                                      <div
                                        key={index}
                                        data-note-id={note?.id}
                                        className={`p-3 rounded-lg relative group/note transition-all duration-300 ${
                                          note?.resolved ? 'bg-muted/30' : 'bg-white/50'
                                        } ${
                                          highlightNoteId &&
                                          Number(highlightNoteId) === Number(note?.id)
                                            ? 'ring-2 ring-primary/70'
                                            : ''
                                        }`}
                                      >
                                        {!editNote?.id &&
                                          (!note?.resolved ? (
                                            <div className="absolute top-2 right-2 flex items-center gap-0.5 z-[99999999] opacity-0 transition-opacity group-hover/note:opacity-100">
                                              <button
                                                title="Mark as resolved"
                                                data-qa={`notebook_${note?.id}_resolve_trigger`}
                                                type="button"
                                                onClick={() =>
                                                  handleChangeResolveStatus(note, true)
                                                }
                                                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-backgrounzd transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-6 w-6 text-muted-foreground hover:text-green-600 hover:bg-muted"
                                              >
                                                <LucideCircleCheck className="lucide lucide-circle-check h-3 w-3" />
                                              </button>

                                              <button
                                                title="Edit note"
                                                data-qa={`notebook_${note?.id}_edit_trigger`}
                                                type="button"
                                                onClick={() => handleEditNote(note)}
                                                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-6 w-6 text-muted-foreground hover:text-foreground"
                                              >
                                                <LucideSquarePen className="lucide lucide-square-pen h-3.5 w-3.5" />
                                              </button>

                                              <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                  <button
                                                    title="Delete note"
                                                    data-qa={`notebook_${note?.id}_delete_trigger`}
                                                    type="button"
                                                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-6 w-6 text-muted-foreground hover:text-destructive"
                                                  >
                                                    <LucideTrash2 className="lucide lucide-trash2 h-3 w-3" />
                                                  </button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent className="md:min-w-[560px]">
                                                  <AlertDialogHeader className="gap-0">
                                                    <AlertDialogTitle className="text-lg font-semibold m-0">
                                                      Delete note
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription className="text-sm text-muted-foreground mt-2">
                                                      Are you sure you want to delete this note?
                                                      This action cannot be undone.
                                                    </AlertDialogDescription>
                                                  </AlertDialogHeader>
                                                  <AlertDialogFooter>
                                                    <AlertDialogCancel
                                                      data-qa={`notebook_${note?.id}_delete_note_cancel_trigger`}
                                                    >
                                                      Cancel
                                                    </AlertDialogCancel>
                                                    <AlertDialogAction
                                                      data-qa={`notebook_${note?.id}_delete_note_yes_trigger`}
                                                      onClick={() => handleDeleteNote(note)}
                                                      variant={'delete'}
                                                    >
                                                      Delete
                                                    </AlertDialogAction>
                                                  </AlertDialogFooter>
                                                </AlertDialogContent>
                                              </AlertDialog>
                                            </div>
                                          ) : (
                                            <>
                                              <div className="absolute top-10 right-2 flex items-center gap-0.5 z-[99999999] opacity-0 group-hover/note:opacity-100 transition-opacity">
                                                <button
                                                  title="Unresolve"
                                                  data-qa={`notebook_${note?.id}_undo_resolve_trigger`}
                                                  type="button"
                                                  onClick={() =>
                                                    handleChangeResolveStatus(note, false)
                                                  }
                                                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-6 w-6 text-muted-foreground hover:text-foreground"
                                                >
                                                  <LucideRotateCcw className="lucide lucide-rotate-ccw h-3 w-3" />
                                                </button>
                                              </div>
                                            </>
                                          ))}

                                        <div className="flex items-start gap-2">
                                          <span className="relative flex overflow-hidden rounded-full h-6 w-6 shrink-0">
                                            <span className="overflow-hidden flex h-full w-full items-center justify-center rounded-full text-xs bg-primary/20">
                                              {note?.creator?.logoId ? (
                                                <img
                                                  alt={`note creator ${note?.creator?.label} image`}
                                                  src={`${Config.fileStoreUrl}${note?.creator?.logoId}`}
                                                />
                                              ) : (
                                                note?.creator?.label || ''
                                              )}
                                            </span>
                                          </span>

                                          <div className="flex-1 min-w-0">
                                            <div
                                              className={`flex items-center gap-2 ${!note?.resolved ? 'pr-[70px]' : ''}`}
                                            >
                                              <span className="text-xs font-medium">
                                                {`${note?.creator?.firstName} ${note?.creator?.lastName}`}
                                              </span>

                                              <span className="text-xs text-muted-foreground">
                                                {noteCreatedAt ||
                                                  noteUpdatedAt ||
                                                  new Date().toLocaleString()}
                                              </span>

                                              {note?.resolved && (
                                                <div className="ml-auto inline-flex items-center gap-1 text-[10px] text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full leading-tight">
                                                  <LucideCircleCheck className="lucide lucide-circle-check h-3 w-3 shrink-0" />
                                                  <span
                                                    style={{
                                                      wordWrap: 'break-word',
                                                      wordBreak: 'break-word',
                                                      whiteSpace: 'break-spaces',
                                                    }}
                                                    className="leading-tight text-left"
                                                  >
                                                    Resolved by @{note?.resolvedBy?.label}
                                                  </span>
                                                </div>
                                              )}
                                            </div>

                                            <div id={`edit_note_${note?.id}`} className="mt-1">
                                              <div className="text-start text-sm whitespace-pre-wrap break-words">
                                                {editNote.id === note?.id ? (
                                                  <>
                                                    <div className="relative">
                                                      <Textarea
                                                        id={`edit_note_${note?.id}_textarea`}
                                                        ref={(el) => {
                                                          if (note?.id) {
                                                            editNoteRefs.current[String(note.id)] =
                                                              el;
                                                          }
                                                        }}
                                                        data-qa={`notebook_${note?.id}_edit_note_textarea`}
                                                        value={editNote.content}
                                                        onKeyDown={(e) => handleKeyDown(e, 'edit')}
                                                        onChange={(e) => handleChangeEditNote(e)}
                                                        onBlur={() => handleBlur('editNote')}
                                                        className={`flex w-full rounded-md border border-input px-3 py-2 ring-offset-background placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[60px] max-h-[calc(50vh-130px)] bg-white text-sm resize-none`}
                                                      />

                                                      {showEditSuggestion(note?.id) && (
                                                        <div
                                                          ref={suggestionsRef}
                                                          className="absolute left-0 right-0 top-full mt-1 bg-popover border border-input rounded-md shadow-lg z-50 max-h-48 overflow-auto"
                                                        >
                                                          {suggestions?.map(
                                                            (i: any, idx: number) => {
                                                              return (
                                                                <button
                                                                  key={idx}
                                                                  ref={(el) =>
                                                                    (suggestionItemRefs.current[
                                                                      idx
                                                                    ] = el)
                                                                  }
                                                                  type="button"
                                                                  onMouseDown={() =>
                                                                    insertMention(i)
                                                                  }
                                                                  className={`w-full px-3 py-2 cursor-pointer flex items-center gap-2 ${suggestions?.[activeIndex]?.userId === i.userId ? 'bg-accent' : 'bg-white'} hover:bg-accent`}
                                                                >
                                                                  <div className="overflow-hidden w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                                                                    {i?.logoId ? (
                                                                      <img
                                                                        alt={`note creator ${i?.firstName[0]}${i?.lastName[0]} image`}
                                                                        src={`${Config.fileStoreUrl}${i?.logoId}`}
                                                                      />
                                                                    ) : (
                                                                      `${i?.firstName[0]}${i?.lastName[0]}`
                                                                    )}
                                                                  </div>
                                                                  <span>
                                                                    {i.firstName +
                                                                      ' ' +
                                                                      i?.lastName}
                                                                  </span>
                                                                  <span className="text-muted-foreground text-xs">
                                                                    @{i.label}
                                                                  </span>
                                                                </button>
                                                              );
                                                            }
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                    <div className="flex gap-1 mt-1">
                                                      <button
                                                        data-qa={`notebook_${note?.id}_save_edit_note_trigger`}
                                                        disabled={!editNote?.content}
                                                        onClick={() => handleUpdateNote()}
                                                        type="button"
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-3 h-6 text-xs"
                                                      >
                                                        <LucideCheck className="lucide lucide-check h-3 w-3 mr-1" />
                                                        Save
                                                      </button>
                                                      <button
                                                        data-qa={`notebook_${note?.id}_cancel_edit_note_trigger`}
                                                        type="button"
                                                        onClick={() => handleEditNote(null)}
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground rounded-md px-3 h-6 text-xs"
                                                      >
                                                        Cancel
                                                      </button>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <span>
                                                    {highlightMentions(note?.content, note)}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                              )}
                              <div ref={bottomRef} style={{ margin: '0 !important' }} />
                            </div>
                          </div>

                          {has_resolved_notes && (
                            <div className="flex items-center justify-end py-1 px-2 border-t border-border/30 bg-[#f5f0d4] leading-normal">
                              <button
                                data-qa={`notebook_filter_resolved_trigger`}
                                type="button"
                                data-resolved={showResolved?.[tab] ? true : false}
                                onClick={() => handleShowResolved()}
                                className="rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors cursor-pointer border border-green-600/40 data-[resolved=true]:border-transparent text-green-600 data-[resolved=true]:bg-green-100 data-[resolved=true]:hover:bg-green-200 data-[resolved=false]:hover:bg-green-50"
                              >
                                {showResolved?.[tab] ? 'Showing' : 'Hiding'} resolved
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex-1 flex items-start justify-center p-4">
                          <span className="text-muted-foreground text-sm">No notes yet</span>
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
            </Tabs>

            {/* Footer with new note actions */}
            <div id="footer" className="p-4 border-t border-border/50">
              {canAccessTeamSettings && (
                <div className="text-xs text-muted-foreground mb-2 text-start">
                  Type @ to mention a teammate • Press Enter for new line.
                </div>
              )}

              <div className="relative">
                <Textarea
                  ref={newNoteRef}
                  data-qa={`notebook_new_note_textarea`}
                  id="new_note_textarea"
                  value={newNoteValue}
                  placeholder="Add a note..."
                  onKeyDown={(e) => handleKeyDown(e, 'new')}
                  onChange={(e) => handleChangeNote(e)}
                  onBlur={(e) => handleBlur('newNote')}
                  className={`flex w-full rounded-md border px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[60px] max-h-[calc(50vh-130px)] bg-white/50 border-border/50 resize-none !pr-20 overflow-y-auto ${!newNoteValue ? 'h-[60px]' : ''}`}
                />

                {showNewSuggestion && (
                  <div
                    ref={suggestionsRef}
                    className="absolute left-0 right-0 bottom-full mb-1 bg-popover border border-input rounded-md shadow-lg z-50 max-h-48 overflow-auto"
                  >
                    {suggestions?.map((i: any, idx: number) => {
                      return (
                        <button
                          key={idx}
                          ref={(el) => (suggestionItemRefs.current[idx] = el)}
                          type="button"
                          onMouseDown={() => insertMention(i)}
                          className={`w-full px-3 py-2 cursor-pointer flex items-center gap-2 ${suggestions?.[activeIndex]?.userId === i.userId ? 'bg-accent' : 'bg-white'} hover:bg-accent`}
                        >
                          <div className="overflow-hidden w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                            {i?.logoId ? (
                              <img
                                alt={`note creator ${i?.firstName[0]}${i?.lastName[0]} image`}
                                src={`${Config.fileStoreUrl}${i?.logoId}`}
                              />
                            ) : (
                              `${i?.firstName[0]}${i?.lastName[0]}`
                            )}
                          </div>
                          <span>{i.firstName + ' ' + i?.lastName}</span>
                          <span className="text-muted-foreground text-xs">@{i.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="absolute right-2 bottom-2 flex items-center gap-1">
                  <button
                    data-qa={`notebook_submit_new_note_trigger`}
                    disabled={!newNoteValue?.trim() || loading}
                    type="button"
                    onClick={() => handleCreateNewNote()}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 h-7 w-7"
                  >
                    <LucideSend className="lucide lucide-send h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
          )}

        {active &&
          closeOnOutsideClick &&
          createPortal(
            <div
              onClick={() => handleCloseNotebook()}
              className={`fixed top-0 left-0 right-0 bottom-0 z-[99999998]`}
            />,
            document.body
          )}
      </>
    );
  }
);

export default Notebook;
